import dompurify from 'dompurify';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Fragment, useState } from 'react';

import { useTabLabels } from '@/hooks/useTabLabels';
import { StyledOrderedList } from '@/Shared/StyledElements';
import { StyledH5, StyledP } from '@/Shared/Typography/typography';
import type { Details } from '@/stores/alignmentStore';
import { hasContent, sanitizeOptions } from '@/utils';

import FocusEntry from '../FocusEntry';

import { StyledAccordionItem } from './AccordionStyles';

interface AccordionProps {
	title: string;
	courseData: Course;
	details: Details;
	accordionKey: string;
}

const Accordion = ({ title, courseData, details, accordionKey }: AccordionProps) => {
	const [isActive, setIsActive] = useState(false);
	const sanitizer = dompurify.sanitize;
	const labels = useTabLabels();

	const mediaResources = courseData?.courses_resources?.filter(
		({ resource }) => resource.resource_type === 'media'
	);
	const textResources = courseData?.courses_resources?.filter(
		({ resource }) => resource.resource_type === 'textbook'
	);
	const hasMediaResources = mediaResources?.length !== 0;
	const hasTextResources = textResources?.length !== 0;

	return (
		<StyledAccordionItem>
			<button
				type="button"
				data-section="header"
				className={
					isActive
						? `accordion-title accordion-title--active course-${courseData?.course_id} ${accordionKey}`
						: `accordion-title course-${courseData?.course_id} ${accordionKey}`
				}
				onClick={() => setIsActive(!isActive)}
			>
				<StyledH5 mb="0" className="card-course-name">
					{title}
				</StyledH5>
				<div>{isActive ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
			</button>
			{isActive && (
				<div className="accordion-content">
					{details.showDescription && (
						<div className="description-block" data-section="description">
							<StyledH5 mb="16px">{labels.description}:</StyledH5>
							{courseData?.course_description && (
								<StyledP
									dangerouslySetInnerHTML={{
										__html: sanitizer(`${courseData?.course_description ?? ''}`, {
											...sanitizeOptions,
										}),
									}}
								/>
							)}
							{hasContent(courseData?.course_prereq) && (
								<>
									<StyledH5 mb="16px">{labels.prereq}:</StyledH5>
									<StyledP
										dangerouslySetInnerHTML={{
											__html: sanitizer(`${courseData?.course_prereq ?? ''}`, {
												...sanitizeOptions,
											}),
										}}
									/>
								</>
							)}
							{hasContent(courseData?.course_extras) && (
								<>
									<StyledH5 mb="16px">{labels.extraInfo}:</StyledH5>
									<StyledP
										dangerouslySetInnerHTML={{
											__html: sanitizer(`${courseData?.course_extras ?? ''}`, {
												...sanitizeOptions,
											}),
										}}
									/>
								</>
							)}
						</div>
					)}
					{details.showOutcomes && (
						<div className="outcomes-block" data-section="outcomes">
							{courseData?.courses_outcomes?.length !== 0 && (
								<StyledH5 mb="16px">{labels.outcomes}:</StyledH5>
							)}
							<StyledOrderedList icon="bullseye">
								{courseData?.courses_outcomes
									?.sort(
										(a: CoursesOutcomesEntity, b: CoursesOutcomesEntity) =>
											a.outcome.outcome_order - b.outcome.outcome_order
									)
									.map(({ outcome }: CoursesOutcomesEntity) => {
										const { outcome_id: id, outcome_text: text } = outcome;
										return (
											<li
												key={id}
												dangerouslySetInnerHTML={{
													__html: sanitizer(`${text}`, {
														...sanitizeOptions,
													}),
												}}
											/>
										);
									})}
							</StyledOrderedList>
						</div>
					)}
					{details.showResources && (
						<div className="resources-block" data-section="resources">
							{hasContent(courseData?.course_resource_info ?? '') && (
								<>
									<StyledH5 mb="16px">{labels.resources}:</StyledH5>
									<StyledP
										dangerouslySetInnerHTML={{
											__html: sanitizer(`${courseData?.course_resource_info}`, {
												...sanitizeOptions,
											}),
										}}
									/>
								</>
							)}
							{hasMediaResources &&
								mediaResources?.map(({ resource }: CoursesResourcesEntity, idx: number) => {
									const {
										resource_id: id,
										resource_title: title,
										resource_author: author,
										resource_detail: detail,
									} = resource;
									return (
										<Fragment key={id}>
											{idx === 0 && <StyledH5 mb="16px">Media</StyledH5>}
											<div style={{ marginBottom: '24px' }}>
												{title && <StyledP mb="0">Title: {title}</StyledP>}
												{author && <StyledP mb="0">Author: {author}</StyledP>}
												{detail && (
													<StyledP mb="0">
														URL:{' '}
														<a
															className="media-link"
															href={detail}
															rel="noreferrer"
															target="_blank"
														>
															Media Link
															<ExternalLink
																size={14}
																style={{ marginLeft: '4px' }}
																color="var(--org-color)"
															/>
														</a>
													</StyledP>
												)}
											</div>
										</Fragment>
									);
								})}
							{hasTextResources &&
								textResources?.map(({ resource }: CoursesResourcesEntity, idx: number) => {
									const {
										resource_id: id,
										resource_title: title,
										resource_isbn: isbn,
										resource_author: author,
										resource_detail: detail,
									} = resource;

									return (
										<Fragment key={id}>
											{idx === 0 && <StyledH5 mb="16px">Textbooks</StyledH5>}
											<div style={{ marginBottom: '24px' }}>
												{title && (
													<StyledP mb="0" fontSize="1.8rem">
														{title}
													</StyledP>
												)}
												{author && <StyledP mb="0">By: {author}</StyledP>}
												{detail && <StyledP mb="0">Description: {detail}</StyledP>}
												{isbn && <StyledP mb="0">ISBN: {isbn}</StyledP>}
											</div>
										</Fragment>
									);
								})}
						</div>
					)}
					{details.showUnits && (
						<div className="units-focus-block" data-section="units">
							{courseData?.courses_focuses?.length !== 0 && (
								<StyledH5 mb="16px">{labels.units}:</StyledH5>
							)}
							<StyledOrderedList icon="brain">
								{courseData?.courses_focuses
									?.sort(
										(a: CourseFocusEntity, b: CourseFocusEntity) =>
											a.focus.focus_order - b.focus.focus_order
									)
									.map(({ focus }: CourseFocusEntity) => {
										const { focus_id: id, focus_title: title, focuses_skills: skills } = focus;
										return (
											<Fragment key={id}>
												<li>
													<StyledP
														mb="16px"
														dangerouslySetInnerHTML={{
															__html: sanitizer(title, {
																...sanitizeOptions,
															}),
														}}
													/>
													<div className="sub-list">
														<FocusEntry type="question" options={skills} title={labels.question} />
														<FocusEntry type="topic" options={skills} title={labels.topic} />
														<FocusEntry type="skill" options={skills} title={labels.skill} />
													</div>
												</li>
											</Fragment>
										);
									})}
							</StyledOrderedList>
						</div>
					)}
				</div>
			)}
		</StyledAccordionItem>
	);
};

export default Accordion;
